import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import Chip from '@/components/base/Chip';
import EmptyState from '@/components/base/EmptyState';
import ContentListItem from '@/components/base/ContentListItem';
import { contentList, type ContentCategory } from '@/mocks/content';
import { searchContent, sortByHot } from '@/utils/content';

const typeFilters: { key: 'all' | ContentCategory; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'news', label: '资讯' },
  { key: 'entertainment', label: '娱乐' },
  { key: 'lifestyle', label: '生活' },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [input, setInput] = useState(q);
  const [type, setType] = useState<'all' | ContentCategory>('all');

  useDocumentTitle(
    q ? `搜索「${q}」- 小多元内容聚合平台` : '全站搜索 - 小多元内容聚合平台',
    '按关键词综合搜索全站的新闻资讯、影视娱乐与生活服务内容，快速找到你感兴趣的信息。',
    '搜索,全站搜索,内容检索,小'
  );

  useEffect(() => {
    setInput(q);
  }, [q]);

  const results = useMemo(() => {
    const base = searchContent(q);
    const filtered = type === 'all' ? base : base.filter((item) => item.category === type);
    return sortByHot(filtered);
  }, [q, type]);

  const hotPicks = useMemo(() => sortByHot(contentList).slice(0, 4), []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    setParams(kw ? { q: kw } : {});
  };

  const quickTags = ['科技', '电影', '旅游', '健康', '财经'];

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-8 md:px-6 md:py-10">
      <h1 className="mb-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">全站搜索</h1>
      <p className="mb-6 text-sm text-foreground-600">输入关键词，一次搜索资讯、娱乐与生活全部内容</p>

      <form onSubmit={submit} className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-3 rounded-md border border-background-300 bg-background-50 px-4 py-3 focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-400"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              type="search"
              name="q"
              placeholder="输入关键词，如：科技、电影、旅游"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-8 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
          >
            <i className="ri-search-line"></i>
            搜索
          </button>
        </div>
      </form>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="text-sm text-foreground-500">热门搜索：</span>
        {quickTags.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setParams({ q: t })}
            className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
          >
            {t}
          </button>
        ))}
      </div>

      {q && (
        <>
          <div className="mb-5 flex flex-col gap-4 border-b border-background-200/70 pb-5 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-foreground-600">
              关键词「<strong className="text-foreground-950">{q}</strong>」共找到{' '}
              <strong className="text-foreground-950">{results.length}</strong> 条结果
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {typeFilters.map((f) => (
                <Chip key={f.key} label={f.label} active={type === f.key} onClick={() => setType(f.key)} />
              ))}
            </div>
          </div>

          {results.length === 0 ? (
            <div>
              <EmptyState
                title={`没有找到与「${q}」相关的内容`}
                description="换一个关键词试试，或者看看以下热门内容，也许会有意外收获。"
              />
              <h2 className="mb-4 mt-10 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-fire-line text-accent-500"></i>
                热门内容推荐
              </h2>
              <div className="rounded-lg border border-background-200/70 bg-background-50 px-5 py-2 md:px-6">
                {hotPicks.map((item) => (
                  <ContentListItem key={item.id} item={item} />
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-background-200/70 bg-background-50 px-5 py-2 md:px-6">
              {results.map((item) => (
                <ContentListItem key={item.id} item={item} />
              ))}
            </div>
          )}
        </>
      )}

      {!q && (
        <div className="mt-8">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-fire-line text-accent-500"></i>
            大家都在看
          </h2>
          <div className="rounded-lg border border-background-200/70 bg-background-50 px-5 py-2 md:px-6">
            {hotPicks.map((item) => (
              <ContentListItem key={item.id} item={item} />
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/news"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-newspaper-line"></i>浏览资讯频道
            </Link>
            <Link
              to="/entertainment"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-movie-2-line"></i>浏览娱乐频道
            </Link>
            <Link
              to="/lifestyle"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-store-2-line"></i>浏览生活频道
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}